import React, { useState } from 'react';
import { addDays } from 'date-fns';
import { z } from 'zod';
import { isoDate, jsDateToIsoDate, isoDateToJsDate } from '../utils/isodate';
import { useSelector } from '../store/utils';
import {
  ChevronLeft,
  ChevronRight,
  KeyboardDoubleArrowRight,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  SvgIcon,
  SvgIconProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormattedDate, useIntl } from 'react-intl';
import { Row } from '../atoms/row';
import { isEqual } from 'lodash';
import { trpc } from '../api/trpc';
import { tooltipMessages } from '../organisms/spot/spot-summary/spot-summary.messages';
import { useLocalStorage } from '../utils/use-session-storage';

// Which price the chart and the summary price row display:
// the bare spot price, the distribution surcharge alone, or their sum.
export enum SpotPriceView {
  WithDistribution = 'with-distribution',
  PriceOnly = 'price-only',
  DistributionOnly = 'distribution-only',
}

const buttonGroupSx = {
  minWidth: { xs: '52px !important', sm: '60px !important' },
  '& svg': { xs: { fontSize: 18 } },
};

export const useSpotSummary = () => {
  const [currentDay, tomorrow] = useSelector(
    ({ variables: { SP_HOURS_PRICE_DATE } }) => {
      return [isoDate(SP_HOURS_PRICE_DATE), getTomorrow()];
    }
  );

  const currentSummary = useSelector(({ variables: s }) => {
    return {
      variant: 'today' as const,
      spotPrices: s.SP_HOURS_PRICE,
      spotPrices15M: s.SP_15M_PRICE,
      spotPricesWithDistribution: s.SP_HOURS_PRICE_WITH_DISTRIBUTION,
      spotPrices15MWithDistribution: s.SP_15M_PRICE_WITH_DISTRIBUTION,
      spotPriceLevel: s.SP_PRICE_LEVEL,
      spotPriceLevel15M: s.SP_PRICE_15M_LEVEL,
      batteryDischarge15M: s.SP_BATTERY_DISCHARGE_TABLE_15M,
      batteryDischarge: s.SP_BATTERY_DISCHARGE_TABLE,
      batteryChargeFromGrid: s.SP_BATTERY_GRID_CHARGE_TABLE,
      batteryChargeFromGrid15M: s.SP_BATTERY_GRID_CHARGE_TABLE_15M,
      batteryDischargeToGrid: s.SP_BATTERY_GRID_DISCHARGE_TABLE,
      batteryDischargeToGrid15M: s.SP_BATTERY_GRID_DISCHARGE_TABLE_15M,
      spotOverflow: s.SP_OVERFLOW_TABLE,
      spotOverflow15M: s.SP_OVERFLOW_15M_TABLE,
      morningOverflow: s.SP_MORNING_OVERFLOW_TABLE,
      morningOverflow15M: s.SP_MORNING_OVERFLOW_15M_TABLE,
      hwHeatingFromGrid: s.SP_HW_GRID_HEAT_TABLE,
      hwHeatingFromGrid15M: s.SP_HW_GRID_HEAT_TABLE_15M,
      heatPumpLevel: s.SP_HP_TABLE,
      batteryChargeFromGridEnabled: s.SP_BATTERY_CONTROL_CUSTOMER,
      batteryDischargeToGridEnabled: s.SP_DISCHARGE_TO_GRID,
      batteryControl: s.PV_BATTERY_CONTROL,
      hwInstalled: s.HW_INSTALL,
      hpInstalled: s.HP_ENABLED,
    };
  }, isEqual);

  const tomorrowSummary = useSelector(({ variables: s }) => {
    const tomorrow = getTomorrow();
    const summaryAvailable =
      s.SP_HOURS_PRICE_TOMORROW_DATE === tomorrow &&
      s.SP_15M_PRICE_TOMORROW_DATE === tomorrow;

    if (!summaryAvailable) {
      return;
    }

    return {
      variant: 'tomorrow' as const,
      spotPrices: s.SP_HOURS_PRICE_TOMORROW,
      spotPrices15M: s.SP_15M_PRICE_TOMORROW,
      spotPricesWithDistribution: s.SP_HOURS_PRICE_WITH_DISTRIBUTION_TOMORROW,
      spotPrices15MWithDistribution: s.SP_15M_PRICE_WITH_DISTRIBUTION_TOMORROW,
    };
  });

  const intl = useIntl();
  const [date, setDate] = useState(() => currentDay);
  const [priceView, setPriceView] = useLocalStorage(
    z.nativeEnum(SpotPriceView),
    'spotPriceView',
    SpotPriceView.WithDistribution
  );

  const dateIsToday = date === currentDay;
  const dateIsTomorrow = date === tomorrow;

  const onPrev = () => {
    setDate(jsDateToIsoDate(addDays(isoDateToJsDate(date), -1)));
  };
  const onNext = () => {
    if (dateIsToday) return setDate(tomorrow);

    setDate(jsDateToIsoDate(addDays(isoDateToJsDate(date), 1)));
  };
  const nextEnabled = date < currentDay || (tomorrowSummary && dateIsToday);

  const getSummary = trpc.spot.getSummary.useQuery({ date });

  const { data, isLoading, isError } = dateIsToday
    ? { data: currentSummary, isLoading: false, isError: false }
    : dateIsTomorrow
    ? { data: tomorrowSummary, isLoading: false, isError: false }
    : {
        data: getSummary.data?.summary
          ? { variant: 'historical' as const, ...getSummary.data.summary }
          : undefined,
        isLoading: getSummary.isLoading,
        isError: getSummary.isError,
      };

  return {
    row: (
      <Row sx={{ px: 1 }}>
        <IconButton
          size="small"
          sx={{ '& svg': { fontSize: 26 } }}
          disabled={!getSummary.data?.prevSummary}
        >
          <ChevronLeft onClick={onPrev} />
        </IconButton>
        <Typography variant="body1" sx={{ mx: 0.5 }}>
          <FormattedDate value={date} />
        </Typography>
        <IconButton
          size="small"
          sx={{ '& svg': { fontSize: 26 } }}
          disabled={!nextEnabled}
          onClick={onNext}
        >
          {(dateIsToday || dateIsTomorrow) && nextEnabled ? (
            <KeyboardDoubleArrowRight />
          ) : (
            <ChevronRight />
          )}
        </IconButton>

        <Box sx={{ ml: 'auto' }}>
          <ButtonGroup variant="outlined">
            <Tooltip title={intl.formatMessage(tooltipMessages.priceBuy)}>
              <Button
                onClick={() => setPriceView(SpotPriceView.WithDistribution)}
                variant={
                  priceView === SpotPriceView.WithDistribution
                    ? 'contained'
                    : 'outlined'
                }
                sx={{ px: 0, ...buttonGroupSx }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Energy />
                  <ElectricalGrid />
                </Box>
              </Button>
            </Tooltip>
            <Tooltip title={intl.formatMessage(tooltipMessages.priceSell)}>
              <Button
                onClick={() => setPriceView(SpotPriceView.PriceOnly)}
                variant={
                  priceView === SpotPriceView.PriceOnly
                    ? 'contained'
                    : 'outlined'
                }
                sx={buttonGroupSx}
              >
                <Energy />
              </Button>
            </Tooltip>
            <Tooltip
              title={intl.formatMessage(tooltipMessages.priceDistribution)}
            >
              <Button
                onClick={() => setPriceView(SpotPriceView.DistributionOnly)}
                variant={
                  priceView === SpotPriceView.DistributionOnly
                    ? 'contained'
                    : 'outlined'
                }
                sx={buttonGroupSx}
              >
                <ElectricalGrid />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>
      </Row>
    ),
    summary: data,
    summaryLoading: isLoading,
    summaryError: isError,
    priceView,
  };
};

function getTomorrow() {
  return jsDateToIsoDate(addDays(new Date(), 1));
}

export const Energy = (props: SvgIconProps) => (
  <SvgIcon
    viewBox="0 0 18 24"
    {...props}
    sx={{ fill: 'none', ...(props.sx || {}) }}
  >
    <path
      d="M15.6152 9.93855H12.6997V2.34597C12.6997 0.736344 12.0315 0.235234 11.6367 0.0833823C11.2419 -0.0684693 10.4067 -0.144395 9.34377 1.07042L0.718603 10.88C-0.192506 11.9278 -0.0406548 12.7022 0.126382 13.0819C0.293419 13.4463 0.779343 14.0689 2.16119 14.0689H5.07674V21.6615C5.07674 23.2711 5.74489 23.7722 6.1397 23.9241C6.26118 23.9696 6.41304 24 6.59526 24C7.05081 24 7.6734 23.7874 8.43266 22.9219L17.0578 13.1274C17.9689 12.0948 17.8323 11.3052 17.6652 10.9408C17.4982 10.5611 17.0123 9.93855 15.6152 9.93855ZM16.1467 12.3378L7.52155 22.1322C7.02044 22.7093 6.656 22.8307 6.56489 22.7852C6.47378 22.7548 6.27637 22.4359 6.27637 21.6615V13.4615C6.27637 13.1274 6.00304 12.8541 5.66896 12.8541H2.17638C1.61453 12.8541 1.31082 12.7174 1.25008 12.5656C1.18934 12.4289 1.28045 12.0948 1.6449 11.6697L10.2549 1.87523C10.7104 1.35894 11.0445 1.20708 11.166 1.20708C11.1812 1.20708 11.1964 1.20708 11.1964 1.20708C11.2875 1.23745 11.4849 1.55634 11.4849 2.33078V10.546C11.4849 10.88 11.7582 11.1534 12.0923 11.1534H15.6152C16.1771 11.1534 16.496 11.3052 16.5567 11.4419C16.6175 11.5785 16.5263 11.9126 16.1467 12.3378Z"
      fill="currentColor"
    />
  </SvgIcon>
);

export const ElectricalGrid = (props: SvgIconProps) => (
  <SvgIcon
    viewBox="0 0 20 24"
    {...props}
    sx={{ fill: 'none', ...(props.sx || {}) }}
  >
    <path
      d="M12.1209 23.6443C12.2347 23.9004 12.505 24.0427 12.7753 23.9715C13.0456 23.9004 13.2306 23.6586 13.2021 23.374L12.4055 11.8933H14.9093V12.8322C14.9093 13.1452 15.1654 13.4013 15.4784 13.4013C15.7913 13.4013 16.0474 13.1452 16.0474 12.8322V11.8933H18.0676V12.8322C18.0676 13.1452 18.3236 13.4013 18.6366 13.4013C18.9496 13.4013 19.2057 13.1452 19.2057 12.8322V11.3242C19.2057 11.182 19.1488 11.0682 19.0776 10.9686C19.0776 10.9686 19.0776 10.9544 19.0776 10.9401L16.5596 8.08062C16.4458 7.95258 16.2893 7.88145 16.1328 7.88145H12.1494L11.8506 3.58506H14.1126V4.32484C14.1126 4.63782 14.3687 4.89389 14.6817 4.89389C14.9947 4.89389 15.2507 4.63782 15.2507 4.32484V3.58506H16.8441V4.32484C16.8441 4.63782 17.1002 4.89389 17.4132 4.89389C17.7261 4.89389 17.9822 4.63782 17.9822 4.32484V3.016C17.9822 2.87374 17.9253 2.75993 17.8542 2.66034C17.8542 2.66034 17.8542 2.64612 17.8542 2.63189L15.6491 0.19917C15.5353 0.0711322 15.3788 0 15.2223 0H3.9265C3.77001 0 3.61351 0.0711322 3.4997 0.19917L1.32306 2.66034C1.32306 2.66034 1.32306 2.67457 1.32306 2.6888C1.2377 2.78838 1.19502 2.90219 1.19502 3.04446V4.35329C1.19502 4.66627 1.4511 4.92235 1.76408 4.92235C2.07706 4.92235 2.33314 4.66627 2.33314 4.35329V3.61351H4.12567V4.35329C4.12567 4.66627 4.38174 4.92235 4.69472 4.92235C5.00771 4.92235 5.26378 4.35329 5.26378 4.35329V3.61351H7.32661L7.02786 7.9099H3.07291C2.91642 7.9099 2.75993 7.98103 2.64612 8.10907L0.128038 10.9544C0.128038 10.9544 0.128038 10.9686 0.128038 10.9828C0.0426793 11.0824 0 11.1962 0 11.3385V12.8465C0 13.1595 0.256076 13.4155 0.569057 13.4155C0.882039 13.4155 1.13811 13.1595 1.13811 12.8465V11.9075H3.38589V12.8465C3.38589 13.1595 3.64197 13.4155 3.95495 13.4155C4.26793 13.4155 4.52401 13.1595 4.52401 12.8465V11.9075H6.74333L5.94665 23.3883C5.93242 23.6586 6.10314 23.9146 6.37344 23.9858C6.41612 23.9858 6.4588 24 6.51571 24C6.74333 24 6.9425 23.872 7.04209 23.6586L9.58862 17.84L12.1351 23.6586L12.1209 23.6443ZM10.2004 16.4173L11.3954 13.6858L11.8506 20.1873L10.2004 16.4173ZM8.08061 9.04801H11.0682L11.182 10.7694H7.95258L8.06639 9.04801H8.08061ZM15.8625 9.04801L17.3705 10.7694H12.3201L12.2063 9.04801H15.8625ZM4.18257 1.13811H14.952L16.1328 2.4754H3.00178L4.18257 1.13811ZM8.4505 3.61351H10.6841L10.9828 7.9099H8.15175L8.4505 3.61351ZM3.32899 9.04801H6.92827L6.81446 10.7694H1.82098L3.34321 9.04801H3.32899ZM10.9259 11.9075L9.57439 14.9947L8.22288 11.9075H10.9259ZM7.29816 20.1873L7.75341 13.6858L8.94843 16.4173L7.29816 20.1873Z"
      fill="currentColor"
    />
  </SvgIcon>
);
